export type CardProvider = 'VISA' | 'HUMO' | 'UZCARD' | 'MASTERCARD' | 'UNIONPAY';

export type CardLike = {
	number?: string | null;
	maskedNumber?: string | null;
	provider?: string | null;
};

function normalizeProvider(raw: string): CardProvider | null {
	const upper = raw.toUpperCase();
	if (upper === 'MC') return 'MASTERCARD';
	if (upper === 'VISA' || upper === 'HUMO' || upper === 'UZCARD' || upper === 'MASTERCARD' || upper === 'UNIONPAY') {
		return upper;
	}
	return null;
}

function inferProviderFromBin(cardNumber: string): CardProvider | null {
	const digits = cardNumber.replace(/\D/g, '');
	if (digits.length < 4) return null;

	if (digits.startsWith('9860')) return 'HUMO';
	if (digits.startsWith('8600')) return 'UZCARD';
	if (digits.startsWith('5416')) return 'MASTERCARD';
	if (digits.startsWith('6262')) return 'UNIONPAY';
	if (digits.startsWith('4073') || digits.startsWith('4040')) return 'VISA';
	if (digits.startsWith('4')) return 'VISA';
	if (digits.startsWith('5')) return 'MASTERCARD';
	return null;
}

export function isFullPan(value: string): boolean {
	return /^\d{16}$/.test(value.replace(/\s/g, ''));
}

export function isShortMask(value: string): boolean {
	return /^\*{2}\d{4}$/.test(value.trim());
}

function countKnownDigits(value: string): number {
	return value.replace(/\D/g, '').length;
}

export function resolveCardDisplayNumber(card: CardLike): string {
	const number = card.number?.trim() ?? '';
	const masked = card.maskedNumber?.trim() ?? '';

	if (number && isFullPan(number)) return number;
	if (masked && isFullPan(masked)) return masked;

	const numberDigits = number ? countKnownDigits(number) : 0;
	const maskedDigits = masked ? countKnownDigits(masked) : 0;

	if (masked && maskedDigits > numberDigits) return masked;
	if (number) return number;
	if (masked) return masked;
	return '—';
}

export function formatCardNumber(card: CardLike): string {
	return resolveCardDisplayNumber(card);
}

export function resolveCardProvider(card: CardLike): CardProvider | null {
	const fromField = card.provider?.trim();
	if (fromField) {
		const normalized = normalizeProvider(fromField);
		if (normalized) return normalized;
	}

	const number = card.number?.trim();
	const displayNumber = resolveCardDisplayNumber(card);
	const binSource =
		number && !isShortMask(number) ? number : displayNumber !== '—' ? displayNumber : number;

	if (!binSource || binSource === '—') return null;

	return inferProviderFromBin(binSource);
}

export function formatPanelCardLabel(card: CardLike): string {
	const number = formatCardNumber(card);
	const provider = resolveCardProvider(card);
	return provider ? `${provider} ${number}` : number;
}
